import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Button, TextField, Chip, Alert,
  Menu, MenuItem, Dialog, DialogContent, useTheme, useMediaQuery,
  Divider, Avatar, IconButton, CircularProgress,
  LinearProgress, Checkbox,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  EditOutlined as EditOutlinedIcon,
  Send as SendIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  CameraAlt as CameraAltIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { MentionsInput, Mention } from 'react-mentions';
import api from '../api';
import { useAuth } from '../AuthContext';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';
import Breadcrumbs from './Breadcrumbs';
import { parseMarkdown } from '../utils/markdown';

export default function WorkOrderDetail() {
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    priorityColors,
    statusColors,
    roleColors,
    borderColor,
    tableRowAlt,
    defaultTypeBackground,
    defaultTypeText,
    pmTypeBackground,
    pmTypeText,
    accentBlue,
    gradientPrimary,
    gradientPrimaryHover,
    isDark,
  } = useThemeColors();
  const [workOrder, setWorkOrder] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { hasRole, user } = useAuth();

  const [priorityAnchor, setPriorityAnchor] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [assignedToAnchor, setAssignedToAnchor] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [tempNotes, setTempNotes] = useState('');

  // Due date edit state
  const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
  const [tempDueDate, setTempDueDate] = useState('');

  // Title/Description edit state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  const [tempDescription, setTempDescription] = useState('');

  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Checklist state
  const [checklistItems, setChecklistItems] = useState([]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Mentions state
  const [mentionUsers, setMentionUsers] = useState([]);

  const formatChipLabel = (text) => {
    if (!text) return 'N/A';
    if (text === 'preventive_maintenance' || text === 'PM') return 'PM';
    if (text === 'in_progress') return 'In Progress';
    return text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Parse comment text — parseMarkdown already handles @[Name](id) mentions
  // and HTML escaping in the correct order
  const parseCommentText = (text) => {
    return parseMarkdown(text);
  };

  // Custom styled chip for priority
  const PriorityChip = ({ priority, onClick, editable }) => {
    const colors = priorityColors[priority] || priorityColors.medium;
    return (
      <Chip
        label={formatChipLabel(priority)}
        size="small"
        onClick={editable ? onClick : undefined}
        onDelete={editable ? onClick : undefined}
        deleteIcon={editable ? <EditOutlinedIcon /> : undefined}
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
          cursor: editable ? 'pointer' : 'default',
          '&:hover': editable ? { opacity: 0.8, boxShadow: 2 } : {},
        }}
      />
    );
  };

  // Custom styled chip for status
  const StatusChip = ({ status, onClick, editable }) => {
    const colors = statusColors[status] || statusColors.assigned;
    return (
      <Chip
        label={formatChipLabel(status)}
        size="small"
        onClick={editable ? onClick : undefined}
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
          cursor: editable ? 'pointer' : 'default',
        }}
      />
    );
  };

  useEffect(() => {
    const init = async () => {
      try {
        const response = await api.get(`/work-orders/${id}`);
        setWorkOrder(response.data);
        setTempNotes(response.data.technician_notes || '');
        loadTechnicians(response.data.assignment_type);
      } catch (err) {
        console.error(err);
      }
    };
    init();
    loadComments();
    loadAttachments();
    loadChecklist();
    loadMentionUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMentionUsers = async () => {
    try {
      const response = await api.get('/users/mentionable');
      setMentionUsers(response.data.map(u => ({ id: String(u.id), display: u.full_name })));
    } catch (err) {
      console.error('Failed to load users for mentions:', err);
    }
  };

  // Helper to check if file is an image
  const isImageFile = (fileType) => {
    return fileType?.startsWith('image/');
  };

  // Image attachments for lightbox
  const imageAttachments = attachments.filter(a => isImageFile(a.file_type));
  const lightboxSlides = imageAttachments.map(a => ({
    src: `${api.defaults.baseURL?.replace('/api', '') || ''}/uploads/${a.filename}`,
    alt: a.original_filename,
  }));

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    setUploadLoading(true);
    setUploadProgress(0);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/uploads/work-order/${id}`, formData, {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      loadAttachments();
      setSuccess('File uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploadLoading(false);
      setUploadProgress(0);
    }
  };

  const loadComments = async () => {
    try {
      const response = await api.get(`/comments/work-order/${id}`);
      setComments(response.data);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setCommentLoading(true);
    try {
      await api.post(`/comments/work-order/${id}`, {
        comment: newComment
      });
      setNewComment('');
      loadComments();
      setSuccess('Comment added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const loadAttachments = async () => {
    try {
      const response = await api.get(`/uploads/work-order/${id}`);
      setAttachments(response.data);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    }
  };

  const loadChecklist = async () => {
    try {
      const response = await api.get(`/work-orders/${id}/checklist`);
      setChecklistItems(response.data);
    } catch (err) {
      console.error('Failed to load checklist:', err);
    }
  };

  const handleToggleChecklistItem = async (itemId) => {
    try {
      const response = await api.patch(`/work-orders/${id}/checklist/${itemId}`);
      setChecklistItems(prev => prev.map(item => item.id === itemId ? response.data : item));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update checklist item');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      await api.delete(`/uploads/attachment/${attachmentId}`);
      loadAttachments();
      setSuccess('Attachment deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const loadWorkOrder = async () => {
    try {
      const response = await api.get(`/work-orders/${id}`);
      setWorkOrder(response.data);
      setTempNotes(response.data.technician_notes || '');
      loadTechnicians(response.data.assignment_type);
    } catch (err) {
      console.error(err);
    }
  };

  const loadTechnicians = async (assignmentType) => {
    try {
      // Load all active users so admins can reassign to anyone
      const response = await api.get('/users/mentionable');
      setTechnicians(response.data);
    } catch (err) {
      console.error('Failed to load technicians:', err);
    }
  };

  const handleUpdatePriority = async (newPriority) => {
    setError('');
    setSuccess('');
    try {
      await api.patch(`/work-orders/${id}/status`, {
        priority: newPriority,
        status: workOrder.status
      });
      setSuccess('Priority updated successfully!');
      loadWorkOrder();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update priority');
    }
    setPriorityAnchor(null);
  };

  const handleUpdateStatus = async (newStatus) => {
    setError('');
    setSuccess('');
    try {
      await api.patch(`/work-orders/${id}/status`, {
        status: newStatus,
        technician_notes: tempNotes
      });
      setSuccess('Status updated successfully!');
      loadWorkOrder();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update status');
    }
    setStatusAnchor(null);
  };

  const handleOpenDueDateDialog = () => {
    // Format date for datetime-local input
    if (workOrder.due_date) {
      const date = new Date(workOrder.due_date);
      const formatted = date.toISOString().slice(0, 16);
      setTempDueDate(formatted);
    } else {
      setTempDueDate('');
    }
    setDueDateDialogOpen(true);
  };

  const handleUpdateDueDate = async () => {
    setError('');
    setSuccess('');
    try {
      await api.patch(`/work-orders/${id}/status`, {
        due_date: tempDueDate || null,
        status: workOrder.status
      });
      setSuccess('Due date updated successfully!');
      loadWorkOrder();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update due date');
    }
    setDueDateDialogOpen(false);
  };

  const handleOpenDetailsDialog = () => {
    setTempTitle(workOrder.title || '');
    setTempDescription(workOrder.description || '');
    setDetailsDialogOpen(true);
  };

  const handleUpdateDetails = async () => {
    setError('');
    setSuccess('');
    try {
      await api.patch(`/work-orders/${id}/status`, {
        title: tempTitle,
        description: tempDescription,
        status: workOrder.status
      });
      setSuccess('Work order details updated successfully!');
      loadWorkOrder();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update details');
    }
    setDetailsDialogOpen(false);
  };

  const handleUpdateAssignedTo = async (newAssignedTo) => {
    setError('');
    setSuccess('');
    try {
      await api.patch(`/work-orders/${id}/status`, {
        assigned_to: newAssignedTo,
        status: workOrder.status
      });
      setSuccess('Assignment updated successfully!');
      loadWorkOrder();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update assignment');
    }
    setAssignedToAnchor(null);
  };

  const handleSaveNotes = async () => {
    setError('');
    setSuccess('');
    try {
      await api.patch(`/work-orders/${id}/status`, { technician_notes: tempNotes });
      setSuccess('Notes saved successfully!');
      loadWorkOrder();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to save notes');
    }
    setNotesDialogOpen(false);
  };

  const handleComplete = async () => {
    if (!window.confirm('Are you sure you want to mark this work order as completed?')) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      await api.patch(`/work-orders/${id}/status`, { status: 'completed', technician_notes: tempNotes });
      setSuccess('Work order marked as completed! Notifications sent to supervisors and administrators.');
      loadWorkOrder();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error completing work order:', err);
      setError(err.response?.data?.error || 'Failed to complete work order');
    }
  };

  if (!workOrder) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Breadcrumbs items={[
        { label: 'Dashboard', path: '/' },
        { label: 'Work Orders', path: '/work-orders' },
        { label: workOrder.work_order_number }
      ]} />

      <Typography variant="h4" gutterBottom sx={{ color: accentBlue, fontWeight: 700, mb: 0 }}>
        {workOrder.work_order_number}
      </Typography>

      {/* Lightbox for image gallery */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Typography variant="h6" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
              Work Order Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography><strong>Asset:</strong> {workOrder.asset_number}</Typography>
                <Typography variant="body2" color="textSecondary">{workOrder.asset_description}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Status:</strong>{' '}
                  <StatusChip
                    status={workOrder.status}
                    onClick={(e) => setStatusAnchor(e.currentTarget)}
                    editable={hasRole('technician', 'janitor', 'it_technician', 'supervisor', 'administrator')}
                  />
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Priority:</strong>{' '}
                  <PriorityChip
                    priority={workOrder.priority}
                    onClick={(e) => setPriorityAnchor(e.currentTarget)}
                    editable={hasRole('administrator', 'supervisor')}
                  />
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Type:</strong>{' '}
                  <Chip
                    label={formatChipLabel(workOrder.type)}
                    size="small"
                    sx={{
                      backgroundColor: workOrder.type === 'preventive_maintenance' ? pmTypeBackground : defaultTypeBackground,
                      color: workOrder.type === 'preventive_maintenance' ? pmTypeText : defaultTypeText,
                      fontWeight: 500,
                    }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography component="div">
                  <strong>Due Date:</strong>{' '}
                  {hasRole('administrator', 'supervisor') ? (
                    <Chip
                      label={workOrder.due_date
                        ? new Date(workOrder.due_date).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })
                        : 'Not set'
                      }
                      size="small"
                      onClick={handleOpenDueDateDialog}
                      onDelete={handleOpenDueDateDialog}
                      deleteIcon={<EditOutlinedIcon />}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: workOrder.due_date && new Date(workOrder.due_date) < new Date()
                          ? '#ffebee'
                          : tableRowAlt,
                        color: workOrder.due_date && new Date(workOrder.due_date) < new Date()
                          ? '#c62828'
                          : 'inherit',
                        border: workOrder.due_date && new Date(workOrder.due_date) < new Date()
                          ? '1px solid #f44336'
                          : 'none',
                        fontWeight: 500,
                        '&:hover': { opacity: 0.8, boxShadow: 2 }
                      }}
                    />
                  ) : (
                    <Chip
                      label={workOrder.due_date
                        ? new Date(workOrder.due_date).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })
                        : 'Not set'
                      }
                      size="small"
                      sx={{
                        backgroundColor: workOrder.due_date && new Date(workOrder.due_date) < new Date()
                          ? '#ffebee'
                          : tableRowAlt,
                        color: workOrder.due_date && new Date(workOrder.due_date) < new Date()
                          ? '#c62828'
                          : 'inherit',
                        border: workOrder.due_date && new Date(workOrder.due_date) < new Date()
                          ? '1px solid #f44336'
                          : 'none',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography sx={{ flex: 1 }}><strong>Title:</strong> {workOrder.title}</Typography>
                  {hasRole('administrator', 'supervisor') && (
                    <IconButton
                      size="small"
                      onClick={handleOpenDetailsDialog}
                      sx={{ color: accentBlue, p: 0.5 }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography><strong>Description:</strong></Typography>
                    <Typography>{workOrder.description || 'N/A'}</Typography>
                  </Box>
                  {hasRole('administrator', 'supervisor') && (
                    <IconButton
                      size="small"
                      onClick={handleOpenDetailsDialog}
                      sx={{ color: accentBlue, p: 0.5 }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Requested By:</strong> {workOrder.requested_by_name || 'System'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  <strong>Assigned To:</strong>{' '}
                  {hasRole('administrator', 'supervisor') ? (
                    <Chip
                      label={workOrder.assigned_to_name || 'Unassigned'}
                      size="small"
                      onClick={(e) => setAssignedToAnchor(e.currentTarget)}
                      onDelete={(e) => setAssignedToAnchor(e.currentTarget)}
                      deleteIcon={<EditOutlinedIcon />}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: tableRowAlt,
                        fontWeight: 500,
                        '&:hover': { opacity: 0.8, boxShadow: 2 }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      {workOrder.assigned_to_name || 'Unassigned'}
                      {workOrder.status !== 'completed' && workOrder.assigned_to !== user?.id && (
                        <Chip
                          label="Assign to Me"
                          size="small"
                          onClick={() => handleUpdateAssignedTo(user?.id)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isDark ? 'rgba(100, 181, 246, 0.15)' : 'rgba(30, 58, 95, 0.08)',
                            color: accentBlue,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            '&:hover': { opacity: 0.8, boxShadow: 1 }
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Typography>
              </Grid>
              {workOrder.technician_notes && (
                <Grid item xs={12}>
                  <Typography><strong>Notes:</strong></Typography>
                  <Paper sx={{ p: 2, backgroundColor: tableRowAlt, borderRadius: 2, mt: 1 }}>
                    <Typography variant="body2">{workOrder.technician_notes}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Checklist Section */}
              {checklistItems.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentBlue }}>
                      Checklist
                    </Typography>
                    <Chip
                      label={`${checklistItems.filter(i => i.is_checked).length}/${checklistItems.length}`}
                      size="small"
                      sx={{
                        backgroundColor: checklistItems.every(i => i.is_checked)
                          ? statusColors.completed?.bg || '#e8f5e9'
                          : tableRowAlt,
                        color: checklistItems.every(i => i.is_checked)
                          ? statusColors.completed?.text || '#2e7d32'
                          : 'text.secondary',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Paper sx={{ borderRadius: 2, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                    {checklistItems.map((item, index) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 2,
                          py: 0.5,
                          borderBottom: index < checklistItems.length - 1 ? `1px solid ${borderColor}` : 'none',
                          backgroundColor: item.is_checked ? (isDark ? 'rgba(46, 125, 50, 0.08)' : 'rgba(46, 125, 50, 0.04)') : 'transparent',
                          cursor: workOrder?.status !== 'completed' ? 'pointer' : 'default',
                          '&:hover': workOrder?.status !== 'completed' ? { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' } : {},
                        }}
                        onClick={() => workOrder?.status !== 'completed' && handleToggleChecklistItem(item.id)}
                      >
                        <Checkbox
                          checked={Boolean(item.is_checked)}
                          disabled={workOrder?.status === 'completed'}
                          size="small"
                          sx={{ color: accentBlue, '&.Mui-checked': { color: accentBlue } }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            textDecoration: item.is_checked ? 'line-through' : 'none',
                            color: item.is_checked ? 'text.secondary' : 'text.primary',
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              )}

              {/* Attachments Thumbnail Strip */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 1,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: isDragging ? 'rgba(30, 58, 95, 0.08)' : tableRowAlt,
                    border: isDragging ? `2px dashed ${navyBlue}` : `1px solid ${borderColor}`,
                    transition: 'all 0.2s ease',
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {/* Thumbnails */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1,
                      overflowX: 'auto',
                      minHeight: 56,
                      '&::-webkit-scrollbar': { height: 4 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: borderColor, borderRadius: 2 },
                    }}
                  >
                    {attachments.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon fontSize="small" /> No photos or files attached
                      </Typography>
                    ) : (
                      attachments.map((attachment, index) => (
                        <Box
                          key={attachment.id}
                          sx={{
                            position: 'relative',
                            flexShrink: 0,
                            cursor: 'pointer',
                            // Only use hover on desktop, mobile shows delete on tap-hold
                            ...(!isMobile && { '&:hover .delete-btn': { opacity: 1 } }),
                          }}
                          onClick={() => {
                            if (isImageFile(attachment.file_type)) {
                              const idx = imageAttachments.findIndex(img => img.id === attachment.id);
                              setLightboxIndex(idx >= 0 ? idx : 0);
                              setLightboxOpen(true);
                            } else {
                              window.open(`${api.defaults.baseURL}/uploads/attachment/${attachment.id}`, '_blank');
                            }
                          }}
                        >
                          {isImageFile(attachment.file_type) ? (
                            <Box
                              component="img"
                              src={`${api.defaults.baseURL?.replace('/api', '')}/uploads/${attachment.filename}`}
                              alt={attachment.original_filename}
                              sx={{
                                width: 56,
                                height: 56,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: `1px solid ${borderColor}`,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                borderRadius: 1,
                                border: `1px solid ${borderColor}`,
                              }}
                            >
                              <DescriptionIcon sx={{ color: accentBlue }} />
                            </Box>
                          )}
                          <IconButton
                            className="delete-btn"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAttachment(attachment.id);
                            }}
                            sx={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 20,
                              height: 20,
                              backgroundColor: '#f44336',
                              color: 'white',
                              opacity: isMobile ? 1 : 0,  // Always visible on mobile
                              transition: 'opacity 0.2s',
                              '&:hover': { backgroundColor: '#c62828' },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      ))
                    )}
                  </Box>

                  {/* Upload progress */}
                  {uploadLoading && (
                    <Box sx={{ minWidth: 60 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  {/* Upload buttons */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    />
                    <input
                      type="file"
                      ref={cameraInputRef}
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      accept="image/*"
                      capture="environment"
                    />
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadLoading}
                      sx={{
                        backgroundColor: isDark ? '#2d2d2d' : '#fff',
                        border: `1px solid ${borderColor}`,
                        '&:hover': { backgroundColor: 'rgba(30, 58, 95, 0.08)' },
                      }}
                    >
                      <AttachFileIcon fontSize="small" sx={{ color: accentBlue }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploadLoading}
                      sx={{
                        backgroundColor: navyBlue,
                        color: 'white',
                        '&:hover': { backgroundColor: '#2c4a6e' },
                      }}
                    >
                      <CameraAltIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Comments Section */}
          <Paper sx={{ p: 3, mt: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CommentIcon sx={{ color: accentBlue }} />
              <Typography variant="h6" sx={{ color: accentBlue, fontWeight: 600 }}>
                Comments
              </Typography>
            </Box>

            {/* Comments List */}
            {comments.length === 0 ? (
              <Typography color="textSecondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                No comments yet. Be the first to add a comment.
              </Typography>
            ) : (
              <Box sx={{ mb: 3 }}>
                {comments.map((comment, index) => (
                  <Box key={comment.id}>
                    <Box sx={{ display: 'flex', gap: 2, py: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: roleColors[comment.author_role]?.bg || defaultTypeBackground,
                          color: roleColors[comment.author_role]?.text || defaultTypeText,
                          border: `2px solid ${roleColors[comment.author_role]?.border || '#ccc'}`,
                          fontWeight: 600,
                          width: 40,
                          height: 40,
                        }}
                      >
                        {comment.author_name?.charAt(0)?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {comment.author_name}
                          </Typography>
                          <Chip
                            label={formatChipLabel(comment.author_role)}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: roleColors[comment.author_role]?.bg || defaultTypeBackground,
                              color: roleColors[comment.author_role]?.text || defaultTypeText,
                              border: `1px solid ${roleColors[comment.author_role]?.border || '#ccc'}`,
                            }}
                          />
                          {comment.recipient_name && (
                            <Typography variant="caption" color="textSecondary">
                              → {comment.recipient_name}
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                            {format(new Date(comment.created_at), 'MMM d, yyyy h:mm a')}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, '& a': { color: accentBlue }, '& code': { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', px: 0.5, borderRadius: 0.5 }, '& strong': { color: accentBlue } }}
                          dangerouslySetInnerHTML={{ __html: parseCommentText(comment.comment) }}
                        />
                      </Box>
                    </Box>
                    {index < comments.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>
            )}

            {/* Add Comment Form */}
            <Box sx={{ mt: 2 }}>
              <MentionsInput
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment... Use @ to mention someone"
                style={{
                  control: {
                    backgroundColor: 'transparent',
                    fontSize: 14,
                    minHeight: 80,
                  },
                  input: {
                    padding: 12,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 8,
                    outline: 'none',
                    color: isDark ? '#ffffff' : '#333333',
                    backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
                  },
                  suggestions: {
                    list: {
                      backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
                      border: `1px solid ${borderColor}`,
                      borderRadius: 8,
                      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)',
                      overflow: 'hidden',
                    },
                    item: {
                      padding: '8px 12px',
                      color: isDark ? '#ffffff' : '#333333',
                      '&focused': {
                        backgroundColor: isDark ? 'rgba(100, 181, 246, 0.2)' : 'rgba(30, 58, 95, 0.1)',
                      },
                    },
                  },
                }}
              >
                <Mention
                  trigger="@"
                  data={mentionUsers}
                  style={{ backgroundColor: isDark ? 'rgba(100, 181, 246, 0.3)' : '#e3f2fd' }}
                  displayTransform={(id, display) => `@${display}`}
                  renderSuggestion={(suggestion) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: accentBlue }}>
                        {suggestion.display?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" sx={{ color: isDark ? '#ffffff' : '#333333' }}>{suggestion.display}</Typography>
                    </Box>
                  )}
                />
              </MentionsInput>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, mb: 2, display: 'block' }}>
                Use **bold**, *italic*, or `code` for formatting. @ to mention users.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: isMobile ? 'stretch' : 'flex-start' }}>
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || commentLoading}
                  startIcon={<SendIcon />}
                  fullWidth={isMobile}
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                    background: gradientPrimary,
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: isMobile ? 1.5 : 1,
                    fontSize: isMobile ? '1rem' : '0.875rem',
                    '&:hover': {
                      background: gradientPrimaryHover,
                    },
                  }}
                >
                  {commentLoading ? 'Sending...' : 'Send Comment'}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Actions Section */}
          {hasRole('technician', 'janitor', 'it_technician', 'supervisor', 'administrator') && (
            <Paper sx={{ p: 3, mt: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
              <Typography variant="h6" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
                Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setNotesDialogOpen(true)}
                    size="large"
                    sx={{
                      borderColor: navyBlue,
                      color: accentBlue,
                      borderRadius: 2,
                      textTransform: 'none',
                      py: isMobile ? 1.5 : 1,
                      fontSize: isMobile ? '1rem' : '0.875rem',
                      '&:hover': {
                        borderColor: navyBlue,
                        backgroundColor: 'rgba(30, 58, 95, 0.08)',
                      },
                    }}
                  >
                    {isMobile ? 'Notes' : 'Technician Notes'}
                  </Button>
                </Grid>
                {workOrder.status !== 'completed' && (
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={isMobile ? null : <CheckCircleIcon />}
                      onClick={handleComplete}
                      size="large"
                      sx={{
                        background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                        borderRadius: 2,
                        textTransform: 'none',
                        py: isMobile ? 1.5 : 1,
                        fontSize: isMobile ? '1rem' : '0.875rem',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                        },
                      }}
                    >
                      {isMobile ? 'Complete' : 'Mark as Complete'}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Priority Menu */}
      <Menu
        anchorEl={priorityAnchor}
        open={Boolean(priorityAnchor)}
        onClose={() => setPriorityAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, mt: 1 } }}
      >
        <MenuItem onClick={() => handleUpdatePriority('low')} sx={{ color: '#2e7d32' }}>Low</MenuItem>
        <MenuItem onClick={() => handleUpdatePriority('medium')} sx={{ color: pmTypeText }}>Medium</MenuItem>
        <MenuItem onClick={() => handleUpdatePriority('high')} sx={{ color: '#e65100' }}>High</MenuItem>
        <MenuItem onClick={() => handleUpdatePriority('emergency')} sx={{ color: '#c62828' }}>Emergency</MenuItem>
      </Menu>

      {/* Status Menu */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, mt: 1 } }}
      >
        <MenuItem onClick={() => handleUpdateStatus('assigned')} sx={{ color: '#e65100' }}>Assigned</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('in_progress')} sx={{ color: pmTypeText }}>In Progress</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('completed')} sx={{ color: '#2e7d32' }}>Completed</MenuItem>
      </Menu>

      {/* Assigned To Menu */}
      <Menu
        anchorEl={assignedToAnchor}
        open={Boolean(assignedToAnchor)}
        onClose={() => setAssignedToAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, mt: 1 } }}
      >
        <MenuItem onClick={() => handleUpdateAssignedTo('')}>
          <em>Unassigned</em>
        </MenuItem>
        {technicians.map((tech) => (
          <MenuItem key={tech.id} onClick={() => handleUpdateAssignedTo(tech.id)}>
            {tech.full_name} ({tech.username})
          </MenuItem>
        ))}
      </Menu>

      {/* Notes Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Technician Notes
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={tempNotes}
            onChange={(e) => setTempNotes(e.target.value)}
            placeholder="Add notes about this work order..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => setNotesDialogOpen(false)}
              size={isMobile ? 'large' : 'medium'}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveNotes}
              variant="contained"
              size={isMobile ? 'large' : 'medium'}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Due Date Edit Dialog */}
      <Dialog
        open={dueDateDialogOpen}
        onClose={() => setDueDateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Edit Due Date
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            type="datetime-local"
            label="Due Date"
            value={tempDueDate}
            onChange={(e) => setTempDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Leave empty to clear the due date. Due dates are automatically set based on priority SLA when work orders are created.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => setDueDateDialogOpen(false)}
              size={isMobile ? 'large' : 'medium'}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDueDate}
              variant="contained"
              size={isMobile ? 'large' : 'medium'}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Title/Description Edit Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Edit Work Order Details
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Title"
            value={tempTitle}
            onChange={(e) => setTempTitle(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description"
            value={tempDescription}
            onChange={(e) => setTempDescription(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => setDetailsDialogOpen(false)}
              size={isMobile ? 'large' : 'medium'}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDetails}
              variant="contained"
              size={isMobile ? 'large' : 'medium'}
              disabled={!tempTitle.trim()}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
